/**
 * Chaves padronizadas de storage e aliases legados.
 * Use sempre estas constantes ao acessar o storage central.
 */

import { storage, StorageArea, GetOptions } from './storage';
import { debugLog, sendToTerminal, sendToTestLog } from '../terminalLogger';

export const Keys = {
  export: {
    currentSaveData: 'cdr:export:currentSaveData',
    selectedItemId: 'cdr:export:selectedItemId',
    selectedExportType: 'cdr:export:selectedExportType', // Tipo de exportação já selecionado
    lastSaveRequest: 'cdr:export:lastSaveRequest',
    lastSaveRequestId: 'cdr:export:lastSaveRequestId',
    overrides: 'cdr:export:overrides',
    activeProfileId: 'cdr:export:activeProfileId',
    initialMetadataSnapshot: 'cdr:export:metadataSnapshot:initial',
    lastMetadataSnapshot: 'cdr:export:metadataSnapshot:last',
    offlineLastManualDir: 'cdr:export:offline:lastManualDir',
    offlineLastRoot: 'cdr:export:offline:lastRoot',
    offlineFallbackRoot: 'cdr:export:offline:fallbackRoot',
  },
  navigation: {
    /** Contexto para retorno da página de exportação → OrderDetails */
    exportReturnContext: 'cdr:navigation:exportReturnContext',
  },
  order: {
    currentPedido: 'cdr:order:currentPedido',
    pedidoConfig: (orcId: number | string) => `cdr:order:pedidoConfig:${orcId}`,
  },
  rules: {
    productRules: (orcId: number | string, oitId: number | string) => `cdr:rules:productRules:${orcId}:${oitId}`,
  },
  prefs: {
    user: 'cdr:prefs:user',
    oneregua: {
      colors: (role: string | undefined | null, userId: string | number | undefined | null) =>
        `cdr:prefs:oneregua:colors:${String(role ?? 'unknown')}:${String(userId ?? 'anon')}`,
    },
    onecontorno: {
      colors: (role: string | undefined | null, userId: string | number | undefined | null) =>
        `cdr:prefs:onecontorno:colors:${String(role ?? 'unknown')}:${String(userId ?? 'anon')}`,
    },
    pedidos: {
      materialFilter: (role: string | undefined | null, userId: string | number | undefined | null) =>
        `cdr:prefs:pedidos:materialFilter:${String(role ?? 'unknown')}:${String(userId ?? 'anon')}`,
      fileStatusFilter: (role: string | undefined | null, userId: string | number | undefined | null) =>
        `cdr:prefs:pedidos:fileStatusFilter:${String(role ?? 'unknown')}:${String(userId ?? 'anon')}`,
      stageFilter: (role: string | undefined | null, userId: string | number | undefined | null) =>
        `cdr:prefs:pedidos:stageFilter:${String(role ?? 'unknown')}:${String(userId ?? 'anon')}`,
    },
  },
  corel: {
    organizedObjects: 'cdr:corel:organizedObjects',
  },
} as const;

// Aliases legados (oldKey -> newKey). Para chaves dinâmicas use prefixos em Migrations.
export const Aliases: Record<string, string> = {
  currentSaveData: Keys.export.currentSaveData,
  currentSelectedItemId: Keys.export.selectedItemId,
  lastSaveRequest: Keys.export.lastSaveRequest,
  lastSaveRequestId: Keys.export.lastSaveRequestId,
  activeExportProfileId: Keys.export.activeProfileId,
  currentPedido: Keys.order.currentPedido,
  corel_organized_objects: Keys.corel.organizedObjects,
  // 'productRules' (genérica) permanece como compat; não há primary direta
};

/**
 * Leitura com aliases: tenta a chave primária e depois uma lista de aliases.
 * Quando encontrar valor em alias, opcionalmente persiste na chave primária.
 */
export function readWithAliases<T = unknown>(
  primaryKey: string,
  aliasKeys: string[],
  opts?: GetOptions<T> & { persistIfAliased?: boolean; areaForPrimary?: StorageArea }
): T | null {
  const area = opts?.area ?? opts?.areaForPrimary ?? 'session';
  const foundPrimary = storage.get<T>(primaryKey, { area, validate: opts?.validate, fallback: undefined as any });
  if (foundPrimary != null) return foundPrimary;

  for (const alias of aliasKeys) {
    // Tentar nas duas áreas por segurança (legados podem ter sido gravados em local)
    const vSession = storage.get<T>(alias, { area: 'session', validate: opts?.validate, fallback: undefined as any });
    if (vSession != null) {
      // Logar alias hits apenas em desenvolvimento ou quando persistIfAliased (migração importante)
      const shouldLog = import.meta.env.MODE !== 'production' || opts?.persistIfAliased;
      if (shouldLog) {
        try {
          debugLog('STORAGE_ALIAS_HIT', { primaryKey, alias, area: 'session' });
          sendToTerminal('STORAGE_ALIAS_HIT', { primaryKey, alias, area: 'session' });
          sendToTestLog('STORAGE_ALIAS_HIT', { primaryKey, alias, area: 'session' }, 'storage');
        } catch {}
      }
      if (opts?.persistIfAliased) storage.set<T>(primaryKey, vSession, { area });
      return vSession;
    }
    const vLocal = storage.get<T>(alias, { area: 'local', validate: opts?.validate, fallback: undefined as any });
    if (vLocal != null) {
      // Logar alias hits apenas em desenvolvimento ou quando persistIfAliased (migração importante)
      const shouldLog = import.meta.env.MODE !== 'production' || opts?.persistIfAliased;
      if (shouldLog) {
        try {
          debugLog('STORAGE_ALIAS_HIT', { primaryKey, alias, area: 'local' });
          sendToTerminal('STORAGE_ALIAS_HIT', { primaryKey, alias, area: 'local' });
          sendToTestLog('STORAGE_ALIAS_HIT', { primaryKey, alias, area: 'local' }, 'storage');
        } catch {}
      }
      if (opts?.persistIfAliased) storage.set<T>(primaryKey, vLocal, { area });
      return vLocal;
    }

    // Fallback extra: ler valor bruto do storage nativo quando não for JSON válido (ex.: strings simples)
    try {
      const rawSession = window.sessionStorage.getItem(alias);
      if (rawSession != null) {
        // Tentar JSON.parse; se falhar, usar raw como string
        let val: any = undefined;
        try { val = JSON.parse(rawSession); } catch { val = rawSession as any; }
        if (val != null) {
          try {
            debugLog('STORAGE_ALIAS_HIT_RAW', { primaryKey, alias, area: 'session' });
            sendToTerminal('STORAGE_ALIAS_HIT_RAW', { primaryKey, alias, area: 'session' });
            sendToTestLog('STORAGE_ALIAS_HIT_RAW', { primaryKey, alias, area: 'session' }, 'storage');
          } catch {}
          if (opts?.persistIfAliased) storage.set<T>(primaryKey, val as T, { area });
          return val as T;
        }
      }
      const rawLocal = window.localStorage.getItem(alias);
      if (rawLocal != null) {
        let val: any = undefined;
        try { val = JSON.parse(rawLocal); } catch { val = rawLocal as any; }
        if (val != null) {
          try {
            debugLog('STORAGE_ALIAS_HIT_RAW', { primaryKey, alias, area: 'local' });
            sendToTerminal('STORAGE_ALIAS_HIT_RAW', { primaryKey, alias, area: 'local' });
            sendToTestLog('STORAGE_ALIAS_HIT_RAW', { primaryKey, alias, area: 'local' }, 'storage');
          } catch {}
          if (opts?.persistIfAliased) storage.set<T>(primaryKey, val as T, { area });
          return val as T;
        }
      }
    } catch { /* noop */ }
  }
  return opts?.fallback ?? null;
}


